import { useUserStore } from '@unifyapps/carbon/auth/UserStoreProvider';
import { ColorScheme } from '@unifyapps/defs/types/interface';
import useEventCallback from '@unifyapps/hooks/useEventCallback';
import { useSystemColorScheme } from '../../../../../hooks/useSystemColorScheme';
import type { ValueOrCondition } from '../../../../../utils/conditionalValues';
import { getConditionalPropertyValue } from '../../../../../utils/conditionalValues';
import type { OnActionArgs, OnActionType } from '../../../../context/ActionsProvider/context';
import { useAppDependencyGraphProvider } from '../../../../context/DependencyGraphProvider';
import { useGlobalAppStateStore } from '../../../../stores/app/GlobalAppStateStore';
import InterfaceHelper from '../../../../stores/InterfaceStore/helper/InterfaceHelper';
import { InterfaceModes } from '../../../../stores/InterfaceStore/types';
import { ComputeContextKeys } from '../../../computeContext/const';
import { useComputeConditionEvaluationGetter } from '../../../useComputeConditionEvaluationGetter';
import { SHOULD_NOT_PROPAGATE_UP, SHOULD_PROPAGATE_UP } from '../../const';
import { useUpdateColorSchemeForPrivateInterface } from './hooks/useUpdateColorSchemeForPrivateInterface';
import { useUpdateColorSchemeForPublicInterface } from './hooks/useUpdateColorSchemeForPublicInterface';

export default function useSetColorScheme() {
  const { setColorSchemeDetails } = useGlobalAppStateStore().hooks.useActions();
  const interfaceRecord = useGlobalAppStateStore().hooks.record.useDetails();
  const isPublicInterface = InterfaceHelper.isPublicInterface(interfaceRecord);
  const getComputedFilters = useComputeConditionEvaluationGetter();
  const mode = useGlobalAppStateStore().hooks.useMode();
  const systemColorScheme = useSystemColorScheme();
  const userStore = useUserStore();
  const isLoggedIn = userStore.hooks.useIsLoggedIn();

  const { updateColorSchemeForPrivateInterface } = useUpdateColorSchemeForPrivateInterface();
  const { updateColorSchemeForPublicInterface } = useUpdateColorSchemeForPublicInterface();

  const {
    actions: { notifyDependencyChange },
  } = useAppDependencyGraphProvider();

  const onSetColorScheme: OnActionType = useEventCallback((args?: OnActionArgs) => {
    const { domEvent, actionContext } = args ?? {};
    const { payload } = (args?.action ?? {}) as {
      payload: { theme: ValueOrCondition<string> } | undefined;
    };

    domEvent?.stopPropagation?.();

    if (!payload?.theme) {
      return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
    }

    const computedColorScheme = getComputedFilters<ValueOrCondition<ColorScheme>>(
      payload.theme as Record<string, unknown>,
      actionContext as Record<string, unknown>,
    );

    const colorScheme = getConditionalPropertyValue({
      value: computedColorScheme,
    });

    if (!colorScheme) {
      return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
    }

    setColorSchemeDetails({
      originalColorScheme: colorScheme,
      effectiveColorScheme: colorScheme === ColorScheme.SYSTEM ? systemColorScheme : colorScheme,
    });
    // todo: Once backend user update call is implemented for external user, update this check to
    // 1. user is present + not external -> set in the separate user preferences object
    // 2. user is present + external i.e. anonymous session -> set in the user object under customProperties.preferences
    // 3. user is not present -> set in search params
    // no private/public interface checks
    if (!isPublicInterface && mode === InterfaceModes.RUNNER && isLoggedIn) {
      updateColorSchemeForPrivateInterface(colorScheme);
    } else {
      updateColorSchemeForPublicInterface(colorScheme);
    }

    notifyDependencyChange({
      id: ComputeContextKeys.THEME,
    });

    return Promise.resolve(SHOULD_PROPAGATE_UP);
  });

  return {
    onSetColorScheme,
  };
}
